import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import Utils from "../../../../../../utils/Utils";
import MainLayout from "../../../../../../components/MainLayout/MainLayout";
import { KeyStats } from "../../../../../../components/matchup-output-tables/KeyStats";
import { RegressionStats } from "../../../../../../components/matchup-output-tables/RegressionStats";
import { ShotTypeStats } from "../../../../../../components/matchup-output-tables/ShotTypeStats";
import { TeamStats } from "../../../../../../components/matchup-output-tables/TeamStats";
import { authGetRequest } from "../../../../../../utils/authGetRequest";
import Head from "next/head";
import { useCookies } from "react-cookie";
import RichTextDisplay from "../../../../../../components/richtext/display";
import { Sphere3D } from "../../../../../../components/icons";
import WinningMetrics from "../../../../../../components/WinningMetrics";
import { getDefYear } from "../../../../../../constants/main";

const arrowWin = (
  <svg width="11" height="14" viewBox="0 0 11 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.499999 7.86602C-0.166668 7.48112 -0.166667 6.51888 0.5 6.13397L9.5 0.937821C10.1667 0.552921 11 1.03405 11 1.80385L11 12.1962C11 12.966 10.1667 13.4471 9.5 13.0622L0.499999 7.86602Z"
      fill="#F26B0B"
    />
  </svg>
);

const Output = () => {
  const router = useRouter();
  const [cookies, setCookie] = useCookies(["token_access, name"]);
  let leagueFromUrl = router.query?.league;
  let leagueName = router.query?.league;
  if (leagueFromUrl === "ncaa") {
    leagueFromUrl = "college_mens";
  } else if (leagueFromUrl === "ncaaw") {
    leagueFromUrl = "college_womens";
  }
  const { teamName, opponentName, site } = router.query;
  const [predictions, setPredictions] = useState(null);
  const [mainResult, setMainResult] = useState(null);
  const [conferenceTeamCount, setConferenceTeamCount] = useState(0);
  const [authToken, setAuthToken] = useState(null);
  const [preview, setPreview] = useState("");
  const [gameId, setGameId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lines, setLines] = useState(null);
  const [matchupData, setMatchupData] = useState({});

  useEffect(() => {
    if (document.cookie.includes("token_access=")) {
      setAuthToken(document.cookie.split("token_access=").pop().split(";").shift());
    }
  }, []);

  const [user, setUser] = useState(null);
  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem("user")));
  }, []);

  const isStandardPlan = Utils.hasStandardPlan(user);
  const isPremiumPlan = Utils.hasPremiumPlan(user) || Utils.isTrader(user);
  const isTrader = Utils.isTrader(user);
  const isPayed = isStandardPlan || isPremiumPlan;
  const [quickWriteUps, setQuickWriteUps] = useState("");
  const [teamKeys, setTeamKeys] = useState({});
  const [teamRegression, setTeamRegression] = useState({});
  const [teamShottypes, setTeamShottypes] = useState({});
  const leagueYear = getDefYear(leagueName);

  useEffect(() => {
    setIsLoading(true);
    fetch(`${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/matchup-preview`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        home: teamName,
        away: opponentName,
        league:
          leagueFromUrl === "college_mens" ? "ncaa" : leagueFromUrl === "college_womens" ? "ncaaw" : leagueFromUrl,
      }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.data) {
          setPreview(response.data.preview);
          setGameId(response.data.game_id);
          return response.data.game_id;
        }
      })
      .then((game_id) => {
        if (game_id) {
          fetch(
            `${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/lines/${
              leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
            }/${game_id}`
          )
            .then((res) => res.json())
            .then((response) => {
              if (!response.error) {
                setLines(response);
              }
              setIsLoading(false);
            });

          fetch(
            `${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/${
              leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
            }/${game_id}/scorecard/unprotected`
          )
            .then((response) => response.json())
            .then((response) => {
              setMatchupData(response);
            })
            .catch(async (err) => {
              console.error("Something went wrong::", err);
            });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    fetch(
      leagueFromUrl === "wnba"
        ? `https://monte-carmelo-wnba-api.replit.app/`
        : `https://monte-carmelo-sq-article-api.replit.app/`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    )
      .then((response) => response.json())
      .then((response) => {
        const writeUps = response?.find(
          (s) =>
            (s.away_team === opponentName && s.home_team === teamName) ||
            (s.away_team === teamName && s.home_team === opponentName)
        );
        setQuickWriteUps(writeUps?.preview || "");
      })
      .catch(async (err) => {
        console.error("Something went wrong::", err);
      });
  }, []);

  useEffect(() => {
    if (teamName && opponentName && authToken && isPayed) {
      authGetRequest(
        `/api/${
          leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
        }/${teamName}/${opponentName}/prediction-sentences`,
        authToken
      )
        .then((response) => response?.data)
        .then((response) => {
          if (response?.data && response?.data?.pick) {
            setPredictions(response.data);
          } else if (typeof response === "string") {
            let parsedObject = JSON.parse(response.replace(/\bNaN\b/g, "null"));
            setPredictions(parsedObject.data);
          }
        })
        .catch(async (err) => {
          if (err.message.includes("401")) {
            try {
              const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/refresh`, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
                body: authToken,
              })
                .then((res) => res.json())
                .then((response) => {
                  if (response.access_token) {
                    setCookie("token_access", response.access_token);
                    setAuthToken(response.access_token);
                  }
                });
            } catch (error) {
              console.error(error);
            }
          }
          console.error(err);
        });
      // authGetRequest(
      //   `/api/${
      //     leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
      //   }/${teamName}/${opponentName}/${site === "true" ? 0 : 1}/prediction-scores`,
      //   authToken
      // )
      //   .then((response) => response.data)
      //   .then((response) => {
      //     if (response?.error) {
      //       authGetRequest(
      //         `/api/${
      //           leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
      //         }/${teamName}/${opponentName}/${site === "true" ? 1 : 0}/prediction-scores`,
      //         authToken
      //       )
      //         .then((response) => response.data)
      //         .then((response) => {
      //           if (response?.error) {
      //           } else if (response.data) {
      //             setMainResult(response.data);
      //           }
      //         });
      //     } else if (response.data) {
      //       setMainResult(response.data);
      //     }
      //   })
      //   .catch(async (err) => {
      //     console.error(err);
      //   });

      // team keys
      authGetRequest(
        `/api/${
          leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
        }/${teamName}/key`,
        authToken
      )
        .then((response) => response.data)
        .then((response) => {
          if (typeof response === "string") {
            let parsedObject = JSON.parse(response.replace(/\bNaN\b/g, "null"));
            setTeamKeys((prev) => ({
              ...prev,
              teamName: parsedObject,
            }));
          } else {
            let parsedObject = response;
            setTeamKeys((prev) => ({
              ...prev,
              teamName: parsedObject,
            }));
          }
        })
        .catch(async (err) => {
          console.error(err);
        });

      authGetRequest(
        `/api/${
          leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
        }/${opponentName}/key`,
        authToken
      )
        .then((response) => response.data)
        .then((response) => {
          if (typeof response === "string") {
            let parsedObject = JSON.parse(response.replace(/\bNaN\b/g, "null"));
            setTeamKeys((prev) => ({
              ...prev,
              opponentName: parsedObject,
            }));
          } else {
            let parsedObject = response;
            setTeamKeys((prev) => ({
              ...prev,
              opponentName: parsedObject,
            }));
          }
        })
        .catch(async (err) => {
          console.error(err);
        });

      // regression
      authGetRequest(
        `/api/${
          leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
        }/${teamName}/regression`,
        authToken
      )
        .then((response) => response.data)
        .then((response) => {
          if (typeof response === "string") {
            let parsedObject = JSON.parse(response.replace(/\bNaN\b/g, "null"));
            setTeamRegression((prev) => ({
              ...prev,
              teamName: parsedObject,
            }));
          } else if (response.data && response.data.length) {
            setTeamRegression((prev) => ({
              ...prev,
              teamName: response,
            }));
          }
        })
        .catch(async (err) => {
          console.error(err);
        });
      authGetRequest(
        `/api/${
          leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
        }/${opponentName}/regression`,
        authToken
      )
        .then((response) => response.data)
        .then((response) => {
          if (typeof response === "string") {
            let parsedObject = JSON.parse(response.replace(/\bNaN\b/g, "null"));
            setTeamRegression((prev) => ({
              ...prev,
              opponentName: parsedObject,
            }));
          } else if (response.data && response.data.length) {
            setTeamRegression((prev) => ({
              ...prev,
              opponentName: response,
            }));
          }
        })
        .catch(async (err) => {
          console.error(err);
        });

      // shottypes
      authGetRequest(
        `/api/${
          leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
        }/${teamName}/shottypes`,
        authToken
      )
        .then((response) => response.data)
        .then((response) => {
          if (typeof response === "string") {
            let parsedObject = JSON.parse(response.replace(/\bNaN\b/g, "null"));
            setTeamShottypes((prev) => ({
              ...prev,
              teamName: parsedObject,
            }));
          } else if (response.data && response.data.length) {
            setTeamShottypes((prev) => ({
              ...prev,
              teamName: response,
            }));
          }
        })
        .catch(async (err) => {
          console.error(err);
        });

      authGetRequest(
        `/api/${
          leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
        }/${opponentName}/shottypes`,
        authToken
      )
        .then((response) => response.data)
        .then((response) => {
          if (typeof response === "string") {
            let parsedObject = JSON.parse(response.replace(/\bNaN\b/g, "null"));
            setTeamShottypes((prev) => ({
              ...prev,
              opponentName: parsedObject,
            }));
          } else if (response.data && response.data.length) {
            setTeamShottypes((prev) => ({
              ...prev,
              opponentName: response,
            }));
          }
        })
        .catch(async (err) => {
          console.error(err);
        });
    } else if (teamName && opponentName) {
      // team keys
      authGetRequest(
        `/api/${
          leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
        }/${teamName}/key/unprotected`,
        authToken
      )
        .then((response) => response.data)
        .then((response) => {
          let parsedObject = response;
          if (typeof response === "string") {
            parsedObject = JSON.parse(response.replace(/\bNaN\b/g, "null"));
          }
          setTeamKeys((prev) => ({
            ...prev,
            teamName: parsedObject,
          }));
        })
        .catch(async (err) => {
          console.error("Something went wrong::", err);
        });

      authGetRequest(
        `/api/${
          leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
        }/${opponentName}/key/unprotected`,
        authToken
      )
        .then((response) => response.data)
        .then((response) => {
          let parsedObject = response;
          if (typeof response === "string") {
            parsedObject = JSON.parse(response.replace(/\bNaN\b/g, "null"));
          }
          setTeamKeys((prev) => ({
            ...prev,
            opponentName: parsedObject,
          }));
        })
        .catch(async (err) => {
          console.error("Something went wrong::", err);
        });
    }
  }, [router, authToken, isPayed, leagueFromUrl, opponentName, teamName, setCookie]);

  useEffect(() => {
    if (teamName && authToken && isPayed) {
      authGetRequest(
        `/api/${
          leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
        }/${leagueYear}/teams/${teamName}/summary`,
        authToken
      )
        .then((response) => response.data)
        .then((response) => {
          if (response) {
            const conference = response.data[0]["Conference"];
            authGetRequest(
              `/api/${
                leagueFromUrl === "ncaa" ? "college_mens" : leagueFromUrl === "ncaaw" ? "college_womens" : leagueFromUrl
              }/${leagueYear}/conferences/${conference}/standings`,
              authToken
            )
              .then((response) => {
                return response.data;
              })
              .then((response) => {
                if (response?.data?.length > 0) {
                  setConferenceTeamCount(response.data.length);
                }
              })
              .catch((err) => {
                console.error("Unable to Load Conference Team Count :(");
              });
          }
        })
        .catch(async (err) => {
          console.error("Error getting data: ", err);
        });
    }
  }, [router, authToken, isPayed, leagueFromUrl, leagueYear, teamName]);
  return (
    <>
      <Head>
        <title>
          {`${teamName} vs ${opponentName} (${site}) Predictions and Picks | ShotQuality Basketball Matchup Analysis`}
        </title>
        <meta
          property="description"
          content={`Game preview of ${teamName} vs ${opponentName}  stats and regression analysis for ${site} ${leagueName} basketball game.`}
        />
        <meta property="keywords" content={`${teamName} vs ${opponentName} basketball game, nba, NCAA`} />
      </Head>

      <MainLayout>
        <main id="ContentBox" style={{ minHeight: "100vh" }}>
          <>
            {isLoading && (
              <div className="loader-container">
                <img src={`/android-chrome-512x512.png`} alt="loading" />
              </div>
            )}
            <div className={`MiscStats Output pt-5 ${!authToken && "Output-logged"}`}>
              <button
                style={{
                  background: "transparent",
                  border: 0,
                  color: "#000",
                  fontSize: "16px",
                  fontWeight: 700,
                }}
                className="outputBackBtn"
                onClick={() => router.back()}
              >
                {"<  "} Back
              </button>

              <section>
                <div className="container-fluid">
                  <div className="row">
                    {/* <div className="col-12 pt-2 pb-4">
                      <h6>
                        {leagueName === "ncaa"
                          ? "NCAA Men's"
                          : leagueName === "ncaaw"
                          ? "NCAA Women's"
                          : leagueName.toUpperCase()}{" "}
                        Basketball Preview: {teamName} vs {opponentName}{" "}
                        {site !== "true" && site !== "false" ? site : ""}
                      </h6>
                    </div> */}
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6">
                      {isPayed && (
                        <div className="mainResult mb-4">
                          <div className="d-flex justify-content-between mt-1">
                            <Link
                              href={`/team/${leagueName}/${leagueYear}/${mainResult?.home || teamName}`}
                              target="_blank"
                              className="scores-team-name-link"
                            >
                              <div className="teamLogoWrapper">
                                <img
                                  src={`http://shotqualitybets.com/static/media/logos/${
                                    mainResult?.home || teamName
                                  }.png`}
                                  alt="home"
                                />
                              </div>
                              <p
                                className="teamName"
                                style={
                                  mainResult?.home_score && mainResult.home_score > mainResult.away_score
                                    ? { color: "#0F131B" }
                                    : null
                                }
                              >
                                {mainResult?.home || teamName}
                              </p>
                            </Link>
                            {!isTrader && mainResult && mainResult?.home_score && (
                              <div
                                className="d-flex align-items-center justify-content-center flex-column text-center"
                                style={{ maxWidth: "17rem" }}
                              >
                                <div className="gameScore">
                                  <span
                                    style={
                                      mainResult.home_score > mainResult.away_score
                                        ? {
                                            marginRight: "8px",
                                            color: "#0F131B",
                                          }
                                        : null
                                    }
                                  >
                                    {mainResult.home_score}
                                  </span>
                                  <div
                                    style={{ display: "flex" }}
                                    className={` ${
                                      mainResult.home_score < mainResult.away_score ? "rodateArrow" : null
                                    }`}
                                  >
                                    {arrowWin}
                                  </div>
                                  <span
                                    style={
                                      mainResult.home_score < mainResult.away_score
                                        ? {
                                            marginRight: "8px",
                                            color: "#0F131B",
                                          }
                                        : { marginLeft: "20px" }
                                    }
                                  >
                                    {" "}
                                    {mainResult.away_score}
                                  </span>
                                </div>
                                <div>
                                  <div>ShotQuality Projection:</div>
                                  <div>
                                    Total: {(mainResult.home_score + mainResult.away_score).toFixed(1)} |{" "}
                                    {mainResult.home_score < mainResult.away_score ? mainResult.away : mainResult.home}{" "}
                                    wins by{" "}
                                    {mainResult.home_score < mainResult.away_score
                                      ? (mainResult.away_score - mainResult.home_score).toFixed(1)
                                      : (mainResult.home_score - mainResult.away_score).toFixed(1)}
                                  </div>
                                </div>
                              </div>
                            )}

                            <Link
                              href={`/team/${leagueName}/${leagueYear}/${mainResult?.away || opponentName}`}
                              target="_blank"
                              className="scores-team-name-link"
                            >
                              <div className="teamLogoWrapper ">
                                <img
                                  src={`http://shotqualitybets.com/static/media/logos/${
                                    mainResult?.away || opponentName
                                  }.png`}
                                  alt="away"
                                />
                              </div>
                              <p
                                className="teamName"
                                style={
                                  mainResult?.home_score && mainResult?.home_score < mainResult?.away_score
                                    ? { color: "#0F131B" }
                                    : null
                                }
                              >
                                {mainResult?.away || opponentName}
                              </p>
                            </Link>
                          </div>

                          <div className="d-flex justify-content-between mt-1"></div>
                          {lines && (
                            <div className="d-flex justify-content-between">
                              <div>
                                {lines.current_spread && (
                                  <b>{lines.current_spread > 0 ? "+" + lines.current_spread : lines.current_spread}</b>
                                )}
                              </div>
                              <div>
                                <div>
                                  <b>OPEN:</b> O/U {lines.opening_total} {lines.opening_spread}
                                </div>
                                <div>
                                  <b>Live:</b> O/U {lines.current_total} {lines.current_spread}
                                </div>
                              </div>
                              <div>
                                {lines.current_spread && (
                                  <b>
                                    {-1 * lines.current_spread > 0
                                      ? "+" + -1 * lines.current_spread
                                      : -1 * lines.current_spread}
                                  </b>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {!isPayed ? (
                        <div className="mainResult mb-4" style={{ padding: "1rem", height: "auto" }}>
                          <div className="d-flex justify-content-between align-items-center gap-1">
                            <div className="teamLogoWrapper">
                              <img src={`http://shotqualitybets.com/static/media/logos/${teamName}.png`} />
                            </div>
                            <span
                              style={{ maxWidth: "60%", color: "#0F131B", fontSize: "16px" }}
                              className="text-center p-2"
                            >
                              Projected ShotQuality Scores are available to existing Premium members.
                            </span>
                            <div className="teamLogoWrapper">
                              <img src={`http://shotqualitybets.com/static/media/logos/${opponentName}.png`} />
                            </div>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="text-center">
                              <Link
                                href={`/team/${leagueName}/${leagueYear}/${teamName}`}
                                target="_blank"
                                className="scores-team-name-link"
                              >
                                <h4>{teamName}</h4>
                              </Link>
                            </span>

                            <span className="text-center">
                              <Link
                                href={`/team/${leagueName}/${leagueYear}/${opponentName}`}
                                target="_blank"
                                className="scores-team-name-link"
                              >
                                <h4>{opponentName}</h4>
                              </Link>
                            </span>
                          </div>
                          <div className="d-flex justify-content-between ">
                            <strong className="pl-2">Home</strong>

                            <Link
                              style={{ maxWidth: "60%", color: "#0F131B" }}
                              href={`/glossary/Score`}
                              target="_blank"
                              className="text-center pt-1"
                            >
                              What is ShotQuality Projected Score?
                            </Link>

                            <strong className="pr-2">Away</strong>
                          </div>
                          {lines && (
                            <div className="text-center">
                              <p>
                                <b>OPEN:</b> O/U {lines.opening_total} {lines.opening_spread}
                              </p>
                            </div>
                          )}
                          {lines && (
                            <div className="d-flex justify-content-between">
                              <div>
                                {lines.current_spread && (
                                  <p>
                                    <b>{lines.current_spread}</b>
                                  </p>
                                )}
                              </div>
                              <div>
                                <p>
                                  <b>Live:</b> O/U {lines.current_total} {lines.current_spread}
                                </p>
                              </div>
                              <div>
                                {lines.current_spread && (
                                  <p>
                                    <b>{-1 * lines.current_spread}</b>
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}

                      <div className="matchup-preview-tables text-center">
                        {gameId && matchupData?.home?.length && matchupData?.away?.length ? (
                          <>
                            <table>
                              <thead>
                                <tr className="" style={{ borderBottom: "1px solid gray" }}>
                                  <td className="table-header-wrap">
                                    {/* <strong>
                                      {matchupData.home[0]["Adj Off SQ Rating bucket"] +
                                        matchupData.home[0]["Offensive_Regression bucket"] +
                                        matchupData.home[0]["Shot Selection bucket"]}
                                    </strong> */}
                                  </td>
                                  <td></td>
                                  <td>
                                    <strong className="pr-2 pl-2 d-inline-block" style={{ maxWidth: "15rem" }}>
                                      Offensive Matchup
                                    </strong>
                                  </td>
                                  <td></td>
                                  <td className="pr-2">
                                    {/* <strong>
                                      {matchupData.away[0]["Adj Off SQ Rating bucket"] +
                                        matchupData.away[0]["Offensive_Regression bucket"] +
                                        matchupData.away[0]["Shot Selection bucket"]}
                                    </strong> */}
                                  </td>
                                </tr>
                              </thead>
                              <tbody>
                                <TableRowWithRating
                                  offBucket={matchupData.home[0]["Adj Off SQ Rating bucket"]}
                                  defBucket={matchupData.away[0]["Adj Off SQ Rating bucket"]}
                                  offRank={matchupData.home[0]["Adj Off SQ Rating rank"]}
                                  defRank={matchupData.away[0]["Adj Off SQ Rating rank"]}
                                  label="Adj SQ Rating"
                                />
                                <TableRowWithRating
                                  offBucket={matchupData.home[0]["Offensive_Regression bucket"]}
                                  defBucket={matchupData.away[0]["Offensive_Regression bucket"]}
                                  offRank={matchupData.home[0]["Offensive_Regression rank"]}
                                  defRank={matchupData.away[0]["Offensive_Regression rank"]}
                                  label="Regression"
                                />
                                <TableRowWithRating
                                  offBucket={matchupData.home[0]["Shot Selection bucket"]}
                                  defBucket={matchupData.away[0]["Shot Selection bucket"]}
                                  offRank={matchupData.home[0]["Shot Selection rank"]}
                                  defRank={matchupData.away[0]["Shot Selection rank"]}
                                  label="Shot Selection"
                                />
                              </tbody>
                            </table>
                            <table>
                              <thead>
                                <tr className="" style={{ borderBottom: "1px solid gray" }}>
                                  <td className="table-header-wrap">
                                    {/* <strong>
                                      {matchupData.away[0]["Adj Def SQ Rating bucket"] +
                                        matchupData.away[0]["Defensive_Regression bucket"] +
                                        matchupData.away[0]["Shot Selection Allowed bucket"]}
                                    </strong> */}
                                  </td>
                                  <td></td>
                                  <td>
                                    <strong className="d-inline-block pr-2 pl-2" style={{ maxWidth: "15rem" }}>
                                      Defensive Matchup
                                    </strong>
                                  </td>
                                  <td></td>
                                  <td className="pr-2">
                                    {/* <strong>
                                      {matchupData.home[0]["Adj Def SQ Rating bucket"] +
                                        matchupData.home[0]["Defensive_Regression bucket"] +
                                        matchupData.home[0]["Shot Selection Allowed bucket"]}
                                    </strong> */}
                                  </td>
                                </tr>
                              </thead>
                              <tbody>
                                <TableRowWithRating
                                  offBucket={matchupData.away[0]["Adj Def SQ Rating bucket"]}
                                  defBucket={matchupData.home[0]["Adj Def SQ Rating bucket"]}
                                  offRank={matchupData.away[0]["Adj Def SQ Rating rank"]}
                                  defRank={matchupData.home[0]["Adj Def SQ Rating rank"]}
                                  label="Adj SQ Rating"
                                />
                                <TableRowWithRating
                                  offBucket={matchupData.away[0]["Defensive_Regression bucket"]}
                                  defBucket={matchupData.home[0]["Defensive_Regression bucket"]}
                                  offRank={matchupData.away[0]["Defensive_Regression rank"]}
                                  defRank={matchupData.home[0]["Defensive_Regression rank"]}
                                  label="Regression"
                                />
                                <TableRowWithRating
                                  offBucket={matchupData.away[0]["Shot Selection Allowed bucket"]}
                                  defBucket={matchupData.home[0]["Shot Selection Allowed bucket"]}
                                  offRank={matchupData.away[0]["Shot Selection Allowed rank"]}
                                  defRank={matchupData.home[0]["Shot Selection Allowed rank"]}
                                  label="Shot Selection Allowed"
                                />
                              </tbody>
                            </table>
                            <table>
                              <thead>
                                <tr className="" style={{ borderBottom: "1px solid gray" }}>
                                  <td className="table-header-wrap">
                                    {/* <strong>
                                      {matchupData.home[0]["pace bucket"] +
                                        matchupData.home[0]["bucket_1"] +
                                        matchupData.home[0]["bucket_2"]}
                                    </strong> */}
                                  </td>
                                  <td></td>
                                  <td>
                                    <strong className="d-inline-block pr-2 pl-2" style={{ maxWidth: "15rem" }}>
                                      Key Matchup Stats
                                    </strong>
                                  </td>
                                  <td></td>
                                  <td className="pr-2">
                                    {/* <strong>
                                      {matchupData.away[0]["pace bucket"] +
                                        matchupData.away[0]["bucket_1"] +
                                        matchupData.away[0]["bucket_2"]}
                                    </strong> */}
                                  </td>
                                </tr>
                              </thead>
                              <tbody>
                                <TableRowWithRating
                                  offBucket={matchupData.home[0]["pace bucket"]}
                                  defBucket={matchupData.away[0]["pace bucket"]}
                                  offRank={matchupData.home[0]["pace rank"]}
                                  defRank={matchupData.away[0]["pace rank"]}
                                  label="Pace"
                                />
                                <TableRowWithRating
                                  offBucket={matchupData.home[0]["bucket_1"]}
                                  defBucket={matchupData.away[0]["bucket_1"]}
                                  label={matchupData.home[0]["name_1"]}
                                  offRank={null}
                                  defRank={null}
                                />
                                <TableRowWithRating
                                  offBucket={matchupData.home[0]["bucket_2"]}
                                  defBucket={matchupData.away[0]["bucket_2"]}
                                  label={matchupData.home[0]["name_2"]}
                                  offRank={null}
                                  defRank={null}
                                />
                              </tbody>
                            </table>
                          </>
                        ) : null}
                      </div>
                    </div>
                    <div className="col-12 col-sm-12 col-md-6 col-lg-6">
                      {quickWriteUps && (
                        <div className="box-shadow-1 mb-3 p-3 b-radius-2">
                          <RichTextDisplay content={quickWriteUps} />
                        </div>
                      )}
                      {preview && (
                        <>
                          <h4>🔥Hot Take:</h4>
                          <hr />
                          <div className=" p-1">
                            <RichTextDisplay content={preview} />
                          </div>
                          <hr />
                        </>
                      )}
                      <div className="matchup-preview-tables text-center">
                        {gameId && matchupData?.home?.length && matchupData?.away?.length ? (
                          <>
                            <table>
                              <thead>
                                <tr className="">
                                  <td className="table-header-wrap">
                                    <a
                                      style={{ color: "black" }}
                                      href={`/team/${leagueName}/${leagueYear}/${teamName}`}
                                      target="_blank"
                                      className="scores-team-name d-inline-block"
                                    >
                                      <strong className="d-block" style={{ maxWidth: "6rem" }}>
                                        {teamName}
                                      </strong>
                                    </a>
                                  </td>
                                  <td>
                                    {" "}
                                    <strong className="pr-2 pl-2 d-inline-block" style={{ maxWidth: "15rem" }}>
                                      When{" "}
                                      <a
                                        style={{ color: "black" }}
                                        href={`/team/${leagueName}/${leagueYear}/${teamName}`}
                                        target="_blank"
                                        className="scores-team-name"
                                      >
                                        <span style={{ fontSize: "1rem", color: "#ef6c00" }}>{teamName}</span>
                                      </a>{" "}
                                      has the ball
                                    </strong>
                                  </td>
                                  <td className="pr-2">
                                    <a
                                      style={{ color: "black" }}
                                      href={`/team/${leagueName}/${leagueYear}/${opponentName}`}
                                      target="_blank"
                                      className="scores-team-name"
                                    >
                                      <strong className="d-inline-block" style={{ maxWidth: "6rem" }}>
                                        {opponentName}
                                      </strong>
                                    </a>
                                  </td>
                                </tr>
                              </thead>
                              <tbody>
                                <TableRowWithGrading
                                  offVal={matchupData.home[0]["Adj Off SQ Rating"]}
                                  defVal={matchupData.home[0]["Adj Def SQ Rating"]}
                                  label="Adj SQ Rating"
                                />
                                <TableRowWithGrading
                                  offVal={matchupData.home[0]["Offensive_Regression"]}
                                  defVal={matchupData.home[0]["Defensive_Regression"]}
                                  label="Regression"
                                  isArrows={true}
                                />
                                <TableRowWithGrading
                                  offVal={matchupData.home[0]["Shot Selection"]}
                                  defVal={matchupData.home[0]["Shot Selection Allowed"]}
                                  label="Shot Selection"
                                />
                                {/* <TableRowWithGrading
                                      offVal={matchupData.home[0]["Shot Selection Allowed"]}
                                      defVal={matchupData.away[0]["Shot Selection Allowed"]}
                                      label="Shot Selection Allowed"
                                    /> */}
                                <TableRowWithGrading
                                  offVal={matchupData.home[0]["value_1"]}
                                  defVal={matchupData.home[0]["opp_value_1"]}
                                  label={matchupData.home[0]["name_1"]}
                                />
                                <TableRowWithGrading
                                  offVal={matchupData.home[0]["value_2"]}
                                  defVal={matchupData.home[0]["opp_value_2"]}
                                  label={matchupData.home[0]["name_2"]}
                                />
                              </tbody>

                              <thead>
                                <tr className="">
                                  <td className="table-header-wrap">
                                    <a
                                      style={{ color: "black" }}
                                      href={`/team/${leagueName}/${leagueYear}/${opponentName}`}
                                      target="_blank"
                                      className="scores-team-name d-inline-block"
                                    >
                                      <strong className="d-block" style={{ maxWidth: "6rem" }}>
                                        {opponentName}
                                      </strong>
                                    </a>
                                  </td>
                                  <td>
                                    {" "}
                                    <strong className="d-inline-block pr-2 pl-2" style={{ maxWidth: "15rem" }}>
                                      When{" "}
                                      <a
                                        style={{ color: "black" }}
                                        href={`/team/${leagueName}/${leagueYear}/${opponentName}`}
                                        target="_blank"
                                        className="scores-team-name"
                                      >
                                        <span style={{ fontSize: "1rem", color: "#ef6c00" }}>{opponentName}</span>
                                      </a>{" "}
                                      has the ball
                                    </strong>
                                  </td>
                                  <td className="pr-2">
                                    <a
                                      style={{ color: "black" }}
                                      href={`/team/${leagueName}/${leagueYear}/${teamName}`}
                                      target="_blank"
                                      className="scores-team-name d-inline-block"
                                    >
                                      <strong className="d-block" style={{ maxWidth: "6rem" }}>
                                        {teamName}
                                      </strong>
                                    </a>
                                  </td>
                                </tr>
                              </thead>
                              <tbody>
                                <TableRowWithGrading
                                  offVal={matchupData.away[0]["Adj Off SQ Rating"]}
                                  defVal={matchupData.away[0]["Adj Def SQ Rating"]}
                                  label="Adj SQ Rating"
                                />
                                <TableRowWithGrading
                                  offVal={matchupData.away[0]["Offensive_Regression"]}
                                  defVal={matchupData.away[0]["Defensive_Regression"]}
                                  label="Regression"
                                  isArrows={true}
                                />
                                {/* <TableRowWithGrading
                                      offVal={matchupData.away[0]["Shot Selection"]}
                                      defVal={matchupData.home[0]["Shot Selection"]}
                                      label="Shot Selection"
                                    /> */}
                                <TableRowWithGrading
                                  offVal={matchupData.away[0]["Shot Selection"]}
                                  defVal={matchupData.away[0]["Shot Selection Allowed"]}
                                  label="Shot Selection Allowed"
                                />
                                <TableRowWithGrading
                                  offVal={matchupData.away[0]["value_1"]}
                                  defVal={matchupData.away[0]["opp_value_1"]}
                                  label={matchupData.away[0]["name_1"]}
                                />
                                <TableRowWithGrading
                                  offVal={matchupData.away[0]["value_2"]}
                                  defVal={matchupData.away[0]["opp_value_2"]}
                                  label={matchupData.away[0]["name_2"]}
                                />
                              </tbody>
                            </table>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4 col-lg-4 first ">
                      {teamKeys?.opponentName && teamKeys?.teamName && (
                        <KeyStats
                          isHome
                          opponentName={opponentName}
                          teamName={teamName}
                          teamKeys={teamKeys}
                          leagueFromUrl={leagueName}
                          leagueYear={leagueYear}
                          isPremiumPlan={isPremiumPlan}
                          user={user}
                          conferenceTeamCount={conferenceTeamCount}
                        />
                      )}

                      <RegressionStats
                        opponentName={opponentName}
                        teamName={teamName}
                        teamRegression={teamRegression}
                        user={user}
                        leagueFromUrl={leagueName}
                      />
                      <ShotTypeStats
                        token={authToken}
                        opponentName={opponentName}
                        teamName={teamName}
                        teamShottypes={teamShottypes}
                        user={user}
                        league={leagueName}
                        conferenceTeamCount={conferenceTeamCount}
                      />
                    </div>
                    <div className="col-md-4 col-lg-4 second">
                      <div className={`gameScoreStats`}>
                        <TeamStats
                          token={authToken}
                          opponentName={opponentName}
                          teamName={teamName}
                          isPremiumPlan={isPremiumPlan}
                          leagueFromUrl={leagueFromUrl}
                        />
                      </div>
                      {predictions && predictions?.pick && (
                        <div className="teamAchievements">
                          <div
                            className="d-flex"
                            style={{
                              padding: "20px 30px",
                              borderBottom: "1px solid #D0D2D9",
                            }}
                          >
                            <div className="teamLogoMiniWrapper" style={{ marginRight: "12px" }}>
                              <img src={`http://shotqualitybets.com/static/media/logos/${predictions.pick}.png`} />
                            </div>
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: 700,
                                color: "#0F131B",
                              }}
                            >
                              {predictions.pick}
                            </span>
                          </div>
                          <ul className="mt-4">
                            <li>{predictions.total}</li>
                            <li>{predictions.cover}</li>
                          </ul>
                        </div>
                      )}
                    </div>
                    {/* Third Row */}
                    <div className="col-md-4 col-lg-4 third">
                      <KeyStats
                        opponentName={opponentName}
                        teamName={teamName}
                        revertDefense
                        teamKeys={teamKeys}
                        leagueFromUrl={leagueName}
                        leagueYear={leagueYear}
                        user={user}
                        isPremiumPlan={isPremiumPlan}
                        conferenceTeamCount={conferenceTeamCount}
                      />
                      <RegressionStats
                        opponentName={opponentName}
                        teamName={teamName}
                        revertDefense
                        teamRegression={teamRegression}
                        user={user}
                        leagueFromUrl={leagueName}
                      />
                      <ShotTypeStats
                        token={authToken}
                        opponentName={opponentName}
                        teamName={teamName}
                        revertDefense
                        teamShottypes={teamShottypes}
                        user={user}
                        league={leagueName}
                        conferenceTeamCount={conferenceTeamCount}
                      />
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </>

          <WinningMetrics targetPage={"Matchups"} />
        </main>
      </MainLayout>
    </>
  );
};

const TableRowWithGrading = ({ offVal, defVal, label, isArrows = false }) => {
  offVal = offVal.toFixed(2);
  defVal = defVal.toFixed(2);
  let offValStr = null;
  let defValStr = null;
  if (isArrows) {
    offValStr =
      parseFloat(offVal) > parseFloat(defVal) ? (
        <span style={{ paddingRight: "1rem" }}>
          <span style={{ color: parseFloat(offVal) < 0 ? "red" : "#549109" }}>&#11165;</span> {offVal}
        </span>
      ) : (
        <span style={{ paddingRight: "1rem" }}>
          <span style={{ color: "red" }}>&#11167;</span> {offVal}
        </span>
      );
    defValStr =
      parseFloat(defVal) > parseFloat(offVal) ? (
        <span style={{ paddingRight: "1rem" }}>
          <span style={{ color: parseFloat(defVal) < 0 ? "#549109" : "red" }}>&#11165;</span> {defVal}
        </span>
      ) : (
        <span style={{ paddingRight: "1rem" }}>
          <span style={{ color: "#549109" }}>&#11167;</span> {defVal}
        </span>
      );
  } else {
    // offVal = offVal > defVal ? <span style={{ color: "#549109" }}>{offVal}</span> : <span>{offVal}</span>;
    // defVal = defVal > offVal ? <span>{defVal}</span> : <span style={{ color: "#549109" }}>{defVal}</span>;
    offValStr = offVal > defVal ? <span>{offVal}</span> : <span>{offVal}</span>;
    defValStr = defVal > offVal ? <span>{defVal}</span> : <span>{defVal}</span>;
  }
  return (
    <tr>
      <td>
        <b>{offValStr}</b>
      </td>
      <td className="text-center">{label}</td>
      <td>
        <b>{defValStr}</b>
      </td>
    </tr>
  );
};
const TableRowWithRating = ({ offBucket, defBucket, offRank, defRank, label }) => {
  const getRankPostFix = (rank) => {
    if (rank === 1) {
      return "st";
    } else if (rank === 2) {
      return "nd";
    } else if (rank === 3) {
      return "rd";
    } else {
      return "th";
    }
  };

  return (
    <tr>
      <td>
        <Sphere3D background={offBucket > 0 ? "#45f746" : "white"} />{" "}
        <Sphere3D background={offBucket > 1 ? "#45f746" : "white"} />{" "}
        <Sphere3D background={offBucket > 2 ? "#45f746" : "white"} />{" "}
        <Sphere3D background={offBucket > 3 ? "#45f746" : "white"} />{" "}
      </td>
      <td>{offRank ? offRank + getRankPostFix(Number(offRank) % 10) : null}</td>
      <td className="text-center">{label}</td>
      <td>{defRank ? defRank + getRankPostFix(Number(defRank) % 10) : null}</td>
      <td>
        <Sphere3D background={defBucket > 0 ? "#45f746" : "white"} />{" "}
        <Sphere3D background={defBucket > 1 ? "#45f746" : "white"} />{" "}
        <Sphere3D background={defBucket > 2 ? "#45f746" : "white"} />{" "}
        <Sphere3D background={defBucket > 3 ? "#45f746" : "white"} />{" "}
      </td>
    </tr>
  );
};
export default Output;
